import { computed, type ComputedRef } from 'vue';

type MaybeOption = Record<string, any> | null | undefined;

/**
 * Property carrying the typed text on the synthetic "create" row that free-text
 * comboboxes offer above their real options.
 *
 * It doubles as the row's marker, which buys two things: label resolution works
 * whatever shape a component's `getOptionLabel` prop takes, and the row is never
 * identified by a missing id — a real option without an id would be
 * indistinguishable under that test.
 */
export const FREE_TEXT_CREATE_LABEL = '__freeTextCreateLabel';

export const isFreeTextCreateOption = (option: MaybeOption): boolean =>
  typeof option?.[FREE_TEXT_CREATE_LABEL] === 'string';

/** The text the user typed, or null when this isn't a create row. */
export const freeTextCreateLabel = (option: MaybeOption): string | null => {
  const label = option?.[FREE_TEXT_CREATE_LABEL];
  return typeof label === 'string' ? label : null;
};

interface FreeTextCreateOptionSources {
  /** whether the host combobox is in free-text mode (its freeTextEntry prop) */
  enabled: () => boolean | undefined;
  /** whether a search for the current text is still in flight */
  loading: () => boolean | undefined;
  /** the current free-text input value, untrimmed */
  text: () => string;
  /** whether an existing option already carries this exact label */
  hasExactMatch: (label: string) => boolean;
}

/**
 * In free-text mode the typed value is a legitimate choice on its own, so it is
 * offered as its own row — otherwise a value that matches nothing leaves the
 * user staring at the "nothing found" placeholder even though their input is
 * perfectly valid.
 *
 * Three cases withhold the row: free text is off, an existing option already
 * carries exactly that label (creating it again would only duplicate it), and a
 * search still being in flight — the item list is stale at that moment, so a
 * match may yet arrive, and calling the value new would let the user create a
 * duplicate of something that does exist.
 *
 * Matching is left to the caller through `hasExactMatch`, since a flat combobox
 * scans a list while a tree one walks its nodes.
 */
export function useFreeTextCreateOption(sources: FreeTextCreateOptionSources): ComputedRef<Record<string, any> | null> {
  return computed(() => {
    if (!sources.enabled() || sources.loading()) return null;

    const typed = sources.text().trim();
    if (!typed) return null;
    if (sources.hasExactMatch(typed)) return null;

    return { [FREE_TEXT_CREATE_LABEL]: typed };
  });
}
